import { type FC, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import type { TaskSnapshot } from "../../types";

import { cancelTasksAsUser } from "../../dashboard-client";
import { useCancellation } from "../../hooks/useCancellation";
import { CANCELLATION_MESSAGES } from "../../utils/cancellationMessages";
import {
  formatUsd,
  getUsageSummaryTotalCostUsd,
  isFiniteNumber,
} from "../../utils/currency";
import {
  getCallbackStatus,
  getProviderColors,
  getStatusDot,
  getTaskProvider,
  isSchemaInvalid,
} from "../../utils/taskStatus";
import { formatRelativeTime } from "../../utils/time";
import { ActivePulseIndicator } from "../common/ActivePulseIndicator";
import { Badge } from "../common/Badge";
import { Button } from "../common/Button";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { DataTable } from "../common/DataTable";
import { ChevronRightIcon } from "../common/icons";
import { isKnownProvider, ProviderIcon } from "../common/ProviderIcon";
import { StatusBadge } from "../common/StatusBadge";
import { CallbackStatusBadge, SchemaInvalidBadge } from "../common/TaskBadges";
import { Text } from "../common/Text";
import { STATUS_LABELS } from "./constants";

interface TaskRowProps {
  task: TaskSnapshot;
  onRefresh?: () => void;
  hideTicketColumn?: boolean;
  hideNodeColumn?: boolean;
}

export const TaskRow: FC<TaskRowProps> = ({
  task,
  onRefresh,
  hideTicketColumn = false,
  hideNodeColumn = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTaskId = searchParams.get("taskId");
  const isSelected = selectedTaskId === task.task_id;

  const callbackStatus = useMemo(() => getCallbackStatus(task), [task]);
  const canCancel = task.status === "queued" || task.status === "running";
  const statusDot = getStatusDot(task.status);
  const rawProvider = getTaskProvider(task);
  const provider =
    rawProvider && isKnownProvider(rawProvider) ? rawProvider : null;
  const providerColors = getProviderColors(rawProvider);
  const statusIcon = (() => {
    if (task.status === "running") {
      return (
        <ActivePulseIndicator
          size={provider ? "md" : "sm"}
          colorClassName={providerColors.pulseColor}
          radarClassName={providerColors.radarColor}
        >
          {provider && (
            <ProviderIcon
              provider={provider}
              className="relative h-full w-full"
            />
          )}
        </ActivePulseIndicator>
      );
    }
    return (
      <span
        aria-hidden="true"
        className={`inline-flex h-2 w-2 rounded-none ${statusDot.color}`}
      />
    );
  })();
  const totalCostUsd =
    task.cost_usd ?? getUsageSummaryTotalCostUsd(task.usage_summary ?? null);
  const hasCost = isFiniteNumber(totalCostUsd);

  const cancellation = useCancellation({
    action: async () => {
      await cancelTasksAsUser({ body: { task_ids: [task.task_id] } });
    },
    messages: CANCELLATION_MESSAGES.task(task.task_id),
    onSuccess: () => {
      if (onRefresh) {
        onRefresh();
      }
    },
  });

  const handleViewDetails = () => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("taskId", task.task_id);
      return newParams;
    });
  };

  return (
    <>
      <ConfirmationDialog
        isOpen={cancellation.itemToCancel !== null}
        title={
          cancellation.itemToCancel !== null
            ? `Cancel Task ${task.task_id}?`
            : ""
        }
        body="This may interrupt in-flight work. Are you sure you want to proceed?"
        onConfirm={() => void cancellation.confirmCancellation()}
        onClose={cancellation.abortCancellation}
        isProcessing={cancellation.isProcessing}
        confirmLabel="Yes, Cancel Task"
      />

      <DataTable.Row
        className={`cursor-pointer ${isSelected ? "bg-slate-50 shadow-[inset_3px_0_0_0_rgb(16_185_129)] dark:bg-emerald-500/5 dark:shadow-[inset_3px_0_0_0_rgb(52_211_153)]" : ""}`}
        onClick={handleViewDetails}
      >
        <DataTable.Cell>
          <div className="flex flex-col gap-1">
            <span className="max-w-[180px] truncate font-mono text-[11px] font-bold tracking-[0.08em] text-slate-900 dark:text-white">
              {task.task_id}
            </span>
            <span className="text-xs text-slate-400 sm:hidden dark:text-slate-500">
              {formatRelativeTime(task.updated_at)}
            </span>
            {hasCost && (
              <Text className="sm:hidden">{formatUsd(totalCostUsd)}</Text>
            )}
          </div>
        </DataTable.Cell>
        <DataTable.Cell>
          <div className="flex flex-col items-start gap-1.5">
            <StatusBadge
              status={task.status}
              label={STATUS_LABELS[task.status]}
              customIcon={statusIcon}
              size="sm"
            />

            {isSchemaInvalid(task) && <SchemaInvalidBadge />}
            {callbackStatus && <CallbackStatusBadge status={callbackStatus} />}
          </div>
        </DataTable.Cell>
        <DataTable.Cell className="hidden text-right md:table-cell">
          {hasCost ? (
            <Badge variant="success" size="sm">
              {formatUsd(totalCostUsd)}
            </Badge>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </DataTable.Cell>
        {!hideTicketColumn && (
          <DataTable.Cell className="hidden md:table-cell">
            <Text className="font-mono">{task.linked_ticket_id ?? "—"}</Text>
          </DataTable.Cell>
        )}
        {!hideNodeColumn && (
          <DataTable.Cell className="hidden md:table-cell">
            <Text className="max-w-[120px] truncate font-mono">
              {task.assigned_node ?? "—"}
            </Text>
          </DataTable.Cell>
        )}
        <DataTable.Cell className="hidden whitespace-nowrap text-xs text-slate-500 sm:table-cell dark:text-slate-400">
          {formatRelativeTime(task.updated_at)}
        </DataTable.Cell>
        <DataTable.Cell className="text-right">
          <div className="flex items-center justify-end gap-2">
            {canCancel && (
              <Button
                variant="cancel"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  cancellation.requestCancellation();
                }}
                loading={cancellation.isProcessing}
              >
                Cancel
              </Button>
            )}
            <div className="text-slate-400 transition-colors group-hover:text-emerald-700 dark:group-hover:text-emerald-300">
              <ChevronRightIcon size={20} className="h-5 w-5" />
            </div>
          </div>
          {cancellation.error && (
            <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
              {cancellation.error}
            </p>
          )}
        </DataTable.Cell>
      </DataTable.Row>
    </>
  );
};
